'use client';

import { Box, Button, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLazyQuery, useQuery } from '@parkchirp/api-client';
import { Dispatch, FC, SetStateAction, useContext, useEffect, useState } from 'react';

import { CommonContext } from '../../../contexts';
import {
	GET_INVOICE_DETAILS,
	GET_INVOICE_HISTORY_CSV,
	GET_INVOICE_LINE_ITEMS,
	GET_INVOICE_LINE_ITEMS_CSV,
	GET_INVOICE_PDF,
} from '../../../queries';
import { HeaderDetails, SetShowDialog, SetShowSnackbar } from '../../../types';
import { isOwnerPortal, paramToSlug } from '../../../utils';
import { SimpleModal } from '../../modals';
import { PaymentLogs } from '../../payments';
import { AdjustInvoiceNotes } from './adjustInvoice';
import AdjustInvoiceContextProvider from './adjustInvoiceContextProvider';
import { formatLineItemEntryAmount } from './helpers';
import { InvoiceDetailsAccordion } from './invoiceDetailsAccordion';
import { InvoiceHistoryTable } from './invoiceHistoryTable';
import { InvoiceLineItemsTable } from './invoiceLineItemsTable';
import { useFetchInvoiceHistoryData } from './useInvoiceHistoryData';

const InvoiceTypeEnum = {
	FIRST_MONTH_INVOICE: 'First Month Invoice',
	AUTO_INVOICE: 'Auto Invoice',
	MANUAL_INVOICE: 'Manual Invoice',
	CARRYOVER: 'Carryover',
	VALIDATION: 'Validation',
	HOTEL_STORAGE: 'Hotel Storage',
	OFFICE_RETAIL_RENT: 'Office Retail Rent',
	MISCELLANEOUS: 'Miscellaneous',
	TRUE_UP: 'True Up',
};

type InvoiceDetailsProps = {
	setShowSnackbar?: SetShowSnackbar;
	setShowDialog?: SetShowDialog;
	userToken: string;
	setHeaderDetails: Dispatch<SetStateAction<HeaderDetails | null>>;
	invoiceId: string;
};

export const InvoiceDetailsPage: FC<InvoiceDetailsProps> = ({
	setHeaderDetails,
	setShowDialog,
	setShowSnackbar,
	userToken,
	invoiceId,
}) => {
	const {
		historyData,
		loading: historyLoading,
		refetch: historyRefetch,
	} = useFetchInvoiceHistoryData(invoiceId, process.env.NEXT_PUBLIC_APP_NAME);
	const [currentTab, setCurrentTab] = useState(0);
	const [showTable, setShowTable] = useState<boolean>(false);
	const [downloadingPDF, setDownloadingPDF] = useState<boolean>(false);
	const [downloadingCSV, setDownloadingCSV] = useState<boolean>(false);
	const [resendingEmail, setResendingEmail] = useState<boolean>(false);
	const [fetchPDFResults] = useLazyQuery(GET_INVOICE_PDF);
	const [fetchInvoiceLineItemsCSVResults] = useLazyQuery(GET_INVOICE_LINE_ITEMS_CSV);
	const [fetchInvoiceHistoryCSVResults] = useLazyQuery(GET_INVOICE_HISTORY_CSV);

	const { AccessControlComponent, hasComponentAccess } = useContext(CommonContext);
	const hasPaymentLogsAccess = hasComponentAccess('InvoiceDetailPaymentLogs');

	const handleChange = (_, newTab: number) => {
		setCurrentTab(newTab);
	};

	const theme = useTheme();
	// need to come up with a type for theme => MUI theme plus extra properties
	const { title } = theme as any;

	const { error, loading, data, refetch } = useQuery(GET_INVOICE_DETAILS, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		variables: {
			invoiceId,
		},
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'network-only',
		skip: !invoiceId,
		onCompleted: (response) => {
			if (response?.getInvoiceDetails) {
				const { subscriptionName, accountId, facilityName, readableId, balance } = response.getInvoiceDetails;
				const leftSideInfo = (
					<Box
						borderLeft={`3px solid ${title.color}`}
						borderRight={`3px solid ${title.color}`}
						padding='20px'
						height='50px'
						display='flex'
						alignItems='center'
					>
						<Typography style={{ fontSize: '15px' }} variant='body1' fontWeight='bold'>
							Invoice Balance: {formatLineItemEntryAmount(balance)}
						</Typography>
					</Box>
				);
				setHeaderDetails({
					info: readableId,
					description: `${subscriptionName} (Account ID: ${accountId}) - ${facilityName}`,
					leftSideInfo,
				});
			}
		},
	});

	const {
		error: lineItemsError,
		loading: lineItemsLoading,
		data: lineItemsData,
		refetch: refetchLineItems,
	} = useQuery(GET_INVOICE_LINE_ITEMS, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		notifyOnNetworkStatusChange: true,
		variables: {
			invoiceId,
		},
		fetchPolicy: 'no-cache',
		skip: !invoiceId,
	});

	const isUnauthorized = [error, lineItemsError].some((queryError) =>
		queryError?.message.toLowerCase().includes('unauthorized'),
	);

	useEffect(() => {
		if (isUnauthorized) {
			setHeaderDetails(null);
		}
	}, [isUnauthorized, setHeaderDetails]);

	async function downloadInvoice(invoiceType) {
		try {
			const response = await fetchPDFResults({
				variables: {
					invoiceId,
					invoiceType: InvoiceTypeEnum[invoiceType],
					facilityId: data.getInvoiceDetails.facilityId,
				},
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
			});
			setShowSnackbar({
				message: 'Successfully downloaded invoice PDF',
				severity: 'success',
			});
			const downloadLink = document.createElement('a');
			downloadLink.href = response.data.getInvoicePDF.pdfUrl;
			downloadLink.download = 'invoice.pdf';
			downloadLink.target = '_blank';
			downloadLink.click();
			setDownloadingPDF(false);
		} catch (err) {
			console.log('err!', err);
			setDownloadingPDF(false);
			setShowSnackbar({
				message: 'An error occurred while retrieving the invoice PDF',
				severity: 'error',
			});
		}
	}

	async function downloadCSV() {
		try {
			const response =
				currentTab === 0
					? await fetchInvoiceLineItemsCSVResults({
							variables: {
								invoiceId,
							},
							context: {
								apiVersion: 'v2',
							},
						})
					: await fetchInvoiceHistoryCSVResults({
							variables: {
								historyData,
							},
							context: {
								apiVersion: 'v2',
							},
						});
			setShowSnackbar({
				message: `Successfully downloaded invoice ${currentTab === 0 ? 'line items' : 'history'} CSV`,
				severity: 'success',
			});
			const downloadLink = document.createElement('a');
			downloadLink.href =
				currentTab === 0 ? response.data.getInvoiceLineItemsCSV.url : response.data.getInvoiceHistoryCSV.url;
			downloadLink.download = currentTab === 0 ? 'invoiceLineItems.csv' : 'invoiceHistory.csv';
			downloadLink.target = '_blank';
			downloadLink.click();
			setDownloadingCSV(false);
		} catch (err) {
			console.log('err!', err);
			setDownloadingCSV(false);
			setShowSnackbar({
				message: 'An error occurred while retrieving the invoice CSV',
				severity: 'error',
			});
		}
	}

	async function resendInvoiceEmail() {
		try {
			const response = await fetch(`${process.env.NEXT_PUBLIC_MONTHLY_API_URL}/invoice_email`, {
				method: 'POST',
				body: JSON.stringify({ invoice_id: invoiceId }),
				headers: {
					Authorization: `Bearer ${userToken}`,
				},
			});

			if (!response.ok) {
				const message = await response.text();
				setShowSnackbar({
					message,
					severity: 'error',
				});
			} else {
				setShowDialog(null);
				setResendingEmail(false);
				setShowSnackbar({
					message: 'Successfully resent email',
					severity: 'success',
				});
			}
		} catch (err) {
			console.log(err);
			setShowSnackbar({
				message: 'An error occurred while sending the email',
				severity: 'error',
			});
		}
	}

	const resendInvoiceText = 'Select confirm to resend the invoice to the subscriber’s email address(es) on file';

	if (isUnauthorized) {
		return (
			<Box
				alignItems='center'
				display='flex'
				flexDirection='column'
				gap={1}
				marginTop='8em'
				role='alert'
				data-cy='invoice-details-unauthorized'
			>
				<Typography variant='h5'>Unauthorized</Typography>
				<Typography variant='body1'>You do not have access to view this invoice.</Typography>
			</Box>
		);
	}

	if (error || lineItemsError) {
		return (
			<Typography variant='body1' align='center'>
				Invoice not found
			</Typography>
		);
	}

	if (loading || lineItemsLoading || !data?.getInvoiceDetails) {
		// TODO mui skeleton once page takes shape
		return <CircularProgress style={{ display: 'block', margin: '50px auto' }} data-cy='loading-indicator' />;
	}

	return (
		<AdjustInvoiceContextProvider>
			<Box style={{ marginTop: '25px' }}>
				<InvoiceDetailsAccordion
					{...{
						...data.getInvoiceDetails,
						refetch,
						setShowDialog,
						setShowSnackbar,
						AccessControlComponent,
					}}
				/>
				<div
					style={{
						display: 'flex',
						width: '50%',
						alignItems: 'center',
						justifyContent: 'space-between',
						marginTop: '20px',
					}}
				>
					{!data.getInvoiceDetails.isDraft && (
						<>
							{!isOwnerPortal && (
								<Button
									variant='contained'
									onClick={() => setShowTable(true)}
									data-cy='adjust-invoice-button'
								>
									Adjust Invoice
								</Button>
							)}
							{!isOwnerPortal && (
								<Button
									variant='contained'
									onClick={() =>
										setShowDialog(
											<SimpleModal
												content={resendInvoiceText}
												title={null}
												dismissText='Close'
												confirmText='Confirm'
												handleDismiss={() => setShowDialog(null)}
												handleConfirm={() => {
													setResendingEmail(true);
													resendInvoiceEmail();
												}}
											/>,
										)
									}
									data-cy='resend-invoice-button'
								>
									Resend Invoice Email{' '}
									{resendingEmail ? <CircularProgress color='inherit' size={20} /> : null}
								</Button>
							)}
							<Button
								variant='contained'
								onClick={() => {
									setDownloadingPDF(true);
									downloadInvoice(data.getInvoiceDetails.type);
								}}
								data-cy='download-pdf-button'
							>
								Download PDF {downloadingPDF ? <CircularProgress color='inherit' size={20} /> : null}
							</Button>
							<Button
								variant='contained'
								onClick={() => {
									setDownloadingCSV(true);
									downloadCSV();
								}}
								data-cy={`download-${paramToSlug(
									currentTab === 0 ? 'InvoiceLineItems' : 'InvoiceHistory',
								)}-csv-button`}
							>
								Download {currentTab === 0 ? 'Invoice Line Items' : 'Invoice History'} CSV
								{downloadingCSV ? <CircularProgress color='inherit' size={20} /> : null}
							</Button>
						</>
					)}
				</div>

				{showTable && (
					<AdjustInvoiceNotes
						{...{
							...data.getInvoiceDetails,
							refetch: () => {
								refetchLineItems();
								refetch();
							},
							setShowTable,
							setShowSnackbar,
						}}
					/>
				)}
				<Tabs
					style={{ marginTop: '20px' }}
					value={currentTab}
					onChange={handleChange}
					aria-label='invoice-details-tabs'
				>
					<Tab label='Invoice Lines' value={0} data-cy='invoice-lines-tab' />
					<Tab label='Invoice History' value={1} data-cy='invoice-history-tab' />
					{hasPaymentLogsAccess && <Tab label='Payment Logs' value={2} data-cy='invoice-payment-logs-tab' />}
				</Tabs>
				{currentTab === 0 ? (
					<InvoiceLineItemsTable
						{...{
							lineItemsError,
							lineItemsLoading,
							lineItemsData,
							timezone: data.getInvoiceDetails.facilityTimezone,
							createdByDailyReport: data.getInvoiceDetails.createdByDailyReport,
							setShowSnackbar,
						}}
					/>
				) : null}
				{currentTab === 1 ? (
					<InvoiceHistoryTable
						timezone={data.getInvoiceDetails.facilityTimezone}
						refetch={historyRefetch}
						loading={historyLoading}
						historyData={historyData}
						setShowDialog={setShowDialog}
						setShowSnackbar={setShowSnackbar}
					/>
				) : null}
				{currentTab === 2 && hasPaymentLogsAccess ? <PaymentLogs invoiceId={invoiceId} /> : null}
			</Box>
		</AdjustInvoiceContextProvider>
	);
};
